import { NavigateFunction } from 'react-router-dom';

import { AnalyticEvent } from ':fat/analytics/events';
import { logError } from ':fat/analytics/logActions';
import { Signer } from ':fat/core/signer';
import { errorToString } from ':fat/helpers/errors';
import { AppRoutes } from ':fat/routing/routes';

import { sanityCheck } from './sanityCheck';

export type HandlerContext = {
  navigate: NavigateFunction;
  signer: Signer | null;
};

export async function handleMessage(
  context: HandlerContext,
  message: TaggedMessage<'securityFrame'>,
) {
  switch (message.kind) {
    case 'PING': {
      void MessageBus.reply(message, { kind: 'PONG' });
      break;
    }
    case 'SANITY_CHECK': {
      const { success, errorMessage } = await sanityCheck();
      void MessageBus.reply(message, {
        kind: 'SANITY_CHECK_RESPONSE',
        success,
        error: errorMessage,
      });
      break;
    }
    case 'SET_DEVTOOL_STATE': {
      window.DEVTOOL_STATE = message.state;
      break;
    }
    case 'GO_TO_FIRST_PAGE': {
      context.navigate(AppRoutes.SECURITY_FRAME.BASE);
      break;
    }
    case 'REQUEST_TRANSACTION_SIGNING': {
      const { signer } = context;
      if (signer) {
        try {
          const signedTxs = await signer.signTransactions(message.txs);
          void MessageBus.reply(message, { kind: 'SIGNED_TRANSACTIONS', signedTxs });
        } catch (err) {
          logError(AnalyticEvent.SignTransactionError, {
            message: errorToString(err),
            transactions: message.txs,
          });
          // eslint-disable-next-line no-console
          console.error(err);
          void MessageBus.reply(
            message,
            { kind: 'SIGNING_ERROR', error: errorToString(err) },
            { rejection: true },
          );
        }
      } else {
        void MessageBus.reply(message, { kind: 'NO_SIGNER' }, { rejection: true });
      }
      break;
    }

    // These messages are replies that are handled by the sender of the
    // original message as an awaited Promise. Nothing to do here, other than
    // convince TypeScript that we've handled all message kinds.
    case 'PONG':
    case 'XPUBS_SAVED':
    case 'XPUB_SAVE_ERROR': {
      break;
    }
    default:
      // @ts-expect-error to guarantee we've handled all message kinds. If
      // this is broken, we missed a possible message. Hover over `message.kind`
      // below to see what was missed.
      throw new Error(`Unexpected message: ${message.kind}`);
  }
}
